import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

import { IWidgetCard, IWidgetCategoriesList, IWidgetInfo } from '@entities/widget-card/model/types';
import {
    IWidgetPaymentData,
    TPaymentInitResponse,
    TWidgetPlan,
} from '@entities/widget-shop/model/types';
import { $api } from '@shared/api';
import { keysToCamel } from '@shared/helpers';
import { IDataResponse } from '@shared/model/types/dataResponse.inteface';

// @ts-ignore
const getWidgetsListUrl = routable.getRoute('getWidgetsList');
// @ts-ignore
const getWidgetPaymentDataUrl = routable.getRoute('getWidgetPlanData');
// @ts-ignore
const payWidgetByCardUrl = routable.getRoute('payWidgetByCard');
// @ts-ignore
const addFreeWidgetUrl = routable.getRoute('addFreeWidget');
// @ts-ignore
const getWidgetInfoUrl = routable.getRoute('getWidgetInfo');
// @ts-ignore
const enableWidgetTrialPeriodUrl = routable.getRoute('enableWidgetTrialPeriod');

const widgetShopApiHandlers = {
    getWidgetCardsList: async ({ signal }: { signal: AbortSignal }) =>
        $api(`${getWidgetsListUrl}`, { signal }),
    getWidgetInfo: async ({ signal }: { signal: AbortSignal }, widgetId: IWidgetCard['id']) =>
        $api(`${getWidgetInfoUrl.replace('_widgetId_', widgetId)}`, { signal }),
    getWidgetPaymentData: async (
        { signal }: { signal: AbortSignal },
        widgetId: IWidgetCard['id']
    ) => $api(`${getWidgetPaymentDataUrl.replace('_widgetId_', widgetId)}`, { signal }),
};

export const widgetShopApi = {
    getWidgetCardsListQueryOptions: () =>
        queryOptions({
            queryKey: ['widgetCards', 'list'],
            queryFn: (meta) => widgetShopApiHandlers.getWidgetCardsList({ signal: meta.signal }),
            select: (data) => {
                const parsedData = keysToCamel<IDataResponse<IWidgetCategoriesList>>(data?.data);
                return parsedData.data;
            },
            staleTime: 60 * 3 * 1000,
            refetchOnMount: false,
            placeholderData: keepPreviousData,
        }),
    getWidgetInfoQueryOptions: ({ widgetId }: { widgetId: IWidgetCard['id'] | null }) =>
        queryOptions({
            queryKey: ['widget', 'info', widgetId],
            queryFn: (meta) =>
                widgetShopApiHandlers.getWidgetInfo(
                    { signal: meta.signal },
                    widgetId as IWidgetCard['id']
                ),
            select: (data) => {
                const parsedData = keysToCamel<
                    IDataResponse<IWidgetInfo<IWidgetPaymentData['paymentData']>>
                >(data?.data);
                return parsedData.data;
            },
            refetchOnMount: false,
            staleTime: 60 * 3 * 1000,
            placeholderData: keepPreviousData,
        }),
    getWidgetPaymentDataQueryOptions: ({ widgetId }: { widgetId: IWidgetCard['id'] }) =>
        queryOptions({
            queryKey: ['widgetPayment', 'paymentData', widgetId],
            queryFn: (meta) =>
                widgetShopApiHandlers.getWidgetPaymentData({ signal: meta.signal }, widgetId),
            select: (data) => {
                const parsedData = keysToCamel<IDataResponse<IWidgetPaymentData>>(data?.data);
                return parsedData.data.paymentData;
            },
            refetchOnMount: false,
            staleTime: 60 * 3 * 1000,
            enabled: !!widgetId,
        }),
    payWidgetByCard: async ({
        formData,
        widgetPlanId,
    }: {
        formData: FormData;
        widgetPlanId: TWidgetPlan['id'];
    }): Promise<AxiosResponse<TPaymentInitResponse>> => {
        const formattedUrl = payWidgetByCardUrl.replace('_widgetPlanId_', widgetPlanId);
        return $api.post(formattedUrl, formData);
    },
    addFreeWidget: async ({
        widgetId,
        notMyCompany = false,
    }: {
        widgetId: IWidgetCard['id'];
        notMyCompany?: boolean;
    }): Promise<AxiosResponse<TPaymentInitResponse>> => {
        const formattedUrl = addFreeWidgetUrl.replace('_widgetId_', widgetId);
        console.log('formattedUrl', formattedUrl);
        const formData = new FormData();
        formData.append('not_my_company', notMyCompany ? '1' : '0');

        return $api.post(formattedUrl, formData);
    },
    enableWidgetTrialPeriod: async ({ widgetId }: { widgetId: IWidgetCard['id'] }) => {
        const formattedUrl = enableWidgetTrialPeriodUrl.replace('_widgetId_', widgetId);
        return $api.post(formattedUrl);
    },
};
